import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Data, Event, Legend, Message, Parameter, State } from './types';

import DropdownParameters from './components/DropdownParameters';

import { InfoWindow } from '@react-google-maps/api';
import { InfoWindowTemplate } from './components/InfoWindowTemplate';
import { Legends } from './components/Legends';
import { clearPolygons, createPolygons, getBoundsPolygons, updatePolygons } from './utils/mapUtils';

import { DashboardWidgetContext } from 'live/context/dashboard-widget';
import { WidgetContext } from 'live/context/widget';
import { GoogleMapsContext, GoogleMapsContextProps } from 'plugin-extension-google/shared/GoogleMaps/GoogleMapsProvider';
import { LayerProp, MAP_HYBRID, MAP_LIGHT, MAP_ROAD } from 'plugin-extension-google/shared/GoogleMaps/LayersMenu';

let prevSpan = undefined
let prevFilterLookup = undefined
const cachedPolygons: { event: Event; polygon: google.maps.Polygon }[]  = []

const CovaregeMap = (props: LiveReactWidgetImplProps<State, Message>): JSX.Element => {

    const { state, widget } = props
    const { zoom, mapType } = widget.jsonConfig
    const { mapInstance, setLayer } = useContext<GoogleMapsContextProps>(GoogleMapsContext)

    const [legends, setLegends] = useState<Legend[]>()
    const [firstZoomLoaded, setFirstZoomLoaded] = useState<boolean>(false)
    const [legendActive, setLegendActive] = useState<Legend>()    
    const [parameterActive, setParameterActive] = useState<Parameter>()
    const [infoWindowProps, setInfoWindowProps] = useState<{ data: Data[], position: google.maps.LatLng }>()    
    const dashboardWidgetContext = useContext(DashboardWidgetContext)
    const widgetContext = useContext(WidgetContext)

    const onClickPolygon = (event: Event, latLng: google.maps.LatLng) => {
        const data = event.data?.map(ele => {
            const parameter = state.metaParams.parameters?.find(param => ele.parameterID === param.id)
            ele.title = parameter?.title

            return ele
        })

        setInfoWindowProps({ data, position: latLng })
    }

    const addListenerOnMap = useCallback(() => {
        if (mapInstance) {
            mapInstance.addListener('click', () => {
                setInfoWindowProps(undefined)
            })
        }
    }, [mapInstance, setFirstZoomLoaded, firstZoomLoaded])

    const fillTitleInLegends = (legends: Legend[] = [], parameters: Parameter[] = []): Legend[] => {
        return legends?.map(ele => {
            const parameter = parameters?.find(param => ele.parameterID === param.id)
            ele.title = parameter?.title
            return ele;
        })
    }

    const fillLegendActive = (parameterID: number, legends: Legend[] = []) => {
        const legend = legends.find(ele => ele.parameterID === parameterID)
        setLegendActive(legend)
    }

    useEffect(() => {
        if (mapInstance) {
            addListenerOnMap()

            if (!legends && state.metaParams.legends?.length > 0) {
                const parameterDefault = state.metaParams.parameters?.find(ele => ele.default === true)

                if (parameterDefault) {
                    const legendsAux = fillTitleInLegends(state.metaParams.legends, state.metaParams.parameters)

                    fillLegendActive(parameterDefault.id, legendsAux)
                    setParameterActive(parameterDefault)
                    setLegends(legendsAux)
                }
            }
        }
    }, [state, mapInstance])

    useEffect(() => {
        updateCachePolygons()
    }, [state?.data])

    useEffect(() => {
        if (parameterActive && cachedPolygons) {
            fillLegendActive(parameterActive.id, legends)
            updatePolygons(parameterActive.id, cachedPolygons)
        }
    }, [parameterActive, cachedPolygons])

    useEffect(() => {
        if (mapType && setLayer) {
            const layers: LayerProp[] = [MAP_HYBRID, MAP_LIGHT, MAP_ROAD]
            setLayer(layers.find(layer => layer.id === +mapType))
        }
    }, [mapType])

    const onLoadInfoWindow = (infowindow: google.maps.InfoWindow) => {
        infowindow.open(mapInstance)
    }

    const updateCachePolygons = useCallback(() => {
        const currentSpan = dashboardWidgetContext?.state?.params?.span ?? widgetContext?.widget?.jsonConfig?.span // Enable to show line in edit mode
        const filter = dashboardWidgetContext?.filters

        if(!mapInstance || !currentSpan || !state?.data ||
            (currentSpan === prevSpan && prevFilterLookup === filter)) return

        prevSpan = currentSpan
        prevFilterLookup = filter
        clearPolygons(cachedPolygons)
        
        const aux: { event: Event, polygon: google.maps.Polygon }[] = createPolygons(mapInstance, state.data, onClickPolygon, cachedPolygons)
        
        const bounds = getBoundsPolygons(aux?.map(ele => ele.polygon))

        if(bounds) mapInstance.fitBounds(bounds)

        aux.forEach((ele, index) => cachedPolygons[index] = ele)
        
        if (zoom){
            const zoomAux = aux.length > 0 ? zoom : 4                
            mapInstance.setZoom(+zoomAux)
        } 
    }, [state?.data, mapInstance, dashboardWidgetContext, widgetContext?.widget?.jsonConfig?.span])
 
    return (<>
        {
            mapInstance &&
            <>
                <WrapperActions>
                    <div>
                        <DropdownParameters values={state.metaParams.parameters} callback={setParameterActive} />
                    </div>
                </WrapperActions>

                {
                    infoWindowProps &&
                    <InfoWindow onLoad={onLoadInfoWindow} position={infoWindowProps.position} onCloseClick={() => setInfoWindowProps(undefined)}>
                        <InfoWindowTemplate paramActiveID={parameterActive?.id} parameters={infoWindowProps.data} />
                    </InfoWindow>
                }

                {
                    legendActive &&
                    <WrapperLegends>
                        <Legends title={legendActive.title} values={legendActive.values} />
                    </WrapperLegends>
                }
            </>
        }

    </>
    )
}

export default React.memo(CovaregeMap)

const WrapperActions = styled.div`
    top: 24px;
    z-index: 0;
    display: flex;
    flex-wrap: wrap;
    position: absolute;
    padding-left: 10px;
    justify-content: end;

    .wrap-item{
        display: flex;
        flex-basis: 100%;
        margin-top: 15px;
        justify-content: end;
        position: absolute;
        top: 49px;
    }
`

const WrapperLegends = styled.div` 
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    position: absolute;
    bottom: 19px;
    left: 10px;
    justify-content: end;
`




